import React, { useMemo } from "react";
import { ExternalLink, MapPin } from "lucide-react";
import {
  listingLocationLabel,
  listingMapEmbedSrc,
  listingMapQuery,
  openGoogleMaps,
} from "@/lib/listingMapQuery";

export function ListingMap({ car, className = "" }) {
  const query = useMemo(() => listingMapQuery(car), [car]);
  const label = useMemo(() => listingLocationLabel(car), [car]);

  if (!query) return null;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
  const embedSrc = listingMapEmbedSrc(car, apiKey);

  const openMaps = () => openGoogleMaps(query);

  return (
    <div className={className} data-testid="detail-map-section">
      <p className="text-xs text-slate-400 mb-2 flex items-start gap-1.5">
        <MapPin className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" />
        <span data-testid="detail-map-address">{label}</span>
      </p>

      <button
        type="button"
        data-testid="detail-map-btn"
        onClick={openMaps}
        className="group relative w-full text-left rounded-xl overflow-hidden border border-white/10 glass focus:outline-none focus-visible:ring-2 focus-visible:ring-electric/60"
        aria-label={`Megnyitás Google Térképen: ${label}`}
      >
        <div className="relative h-36 sm:h-40 w-full bg-anthracite-900/80">
          {embedSrc ? (
            <>
              <iframe
                title={`Térkép: ${label}`}
                data-testid="detail-map-embed"
                src={embedSrc}
                className="absolute inset-0 w-full h-full border-0 pointer-events-none scale-[1.02]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                tabIndex={-1}
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent pointer-events-none"
                aria-hidden
              />
            </>
          ) : (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 bg-anthracite-900"
              data-testid="detail-map-fallback"
            >
              <MapPin className="w-8 h-8 text-electric/80" strokeWidth={1.5} />
              <span className="text-xs text-slate-300 text-center line-clamp-2">{label}</span>
            </div>
          )}

          <div className="absolute bottom-0 inset-x-0 px-3 py-2 flex items-center justify-between gap-2 bg-black/45 backdrop-blur-sm border-t border-white/10">
            <span className="text-[11px] text-slate-200 group-hover:text-white transition-colors">
              Kattints — megnyílik a Google Térkép
            </span>
            <ExternalLink className="w-3.5 h-3.5 text-electric shrink-0 opacity-90 group-hover:opacity-100" />
          </div>
        </div>
      </button>
    </div>
  );
}
